import {FC, useState, useEffect} from 'react'
import {useDispatch} from 'react-redux'
import {useForm, FormProvider, UseFormSetValue, useFieldArray} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import {keys, pickBy} from 'lodash'
import {reduce} from 'lodash/fp'
import moment from 'moment'
import {useLazyQuery} from 'hooks/graphql/'
import {notifySuccess} from 'store/actions/notify'
import {asyncError} from 'store/actions/asyncError'
import Button from 'jbc-front/components/Button'
import {Modal} from 'jbc-front/components/presenters/ui/Modal'
import {Loading} from 'jbc-front/components/presenters/ui/Loading'
import {Section} from 'jbc-front/components/presenters/form/Section'
import {Spacer} from 'jbc-front/components/presenters/Spacer'
import {Label} from 'jbc-front/components/presenters/form/Label'
import {ButtonRow} from 'jbc-front/components/presenters/layout/ButtonRow'
import {Required} from 'jbc-front/components/presenters/form/Label'
import {Input} from 'jbc-front/components/presenters/form/Input'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {DateField} from 'components/reactHookForm/DateField'
import {FileField} from 'components/reactHookForm/FileField'
import {PROCEDURE_NAMES} from 'consts/egovProcedures'
import {
  PREPARE_CSV_PROCEDURE,
  ASYNC_PREPARE_CSV_PROCEDURE,
  PrepareCsvProcedureSchema,
  schema,
  ProcedureKind,
  Attachment,
  QueryResult,
  QueryVariables,
  CSV_EGOV_INFORMATION,
  RecommendedAttachment,
} from '../../query'
import styles from './EgovPrepareModal.scss'

// @ts-ignore
import ListGroup from 'jbc-front/components/ListGroup'
// @ts-ignore
import {useMutation} from 'components/Graphql'

const MAX_SELECT_COUNT = 399

type Item = {
  name: string
}

type EgovPrepareModalProps = {
  selected: {[key: number]: Item}
  procedureKind: ProcedureKind
  setValidationErrors: (errors: string[]) => void
  refetch: () => void
  isTaskRunning: boolean
  isActedUser: boolean
  officeIds: string[]
}

const insuranceTypeByProcedureKind = (procedureKind: ProcedureKind) => {
  switch (procedureKind) {
    case 'r1':
    case 'r4':
    case 'r6':
    case 'r7':
      return 'social_insurance'
    case 'r57':
    case 'r58':
    case 'r60':
    case 'r62':
      return 'employment_insurance'
  }
}

type AttachmentProps = {
  attachments: Attachment[]
  index: number
  setValue: UseFormSetValue<PrepareCsvProcedureSchema>
}

const AttachmentFileField: FC<AttachmentProps> = ({attachments, index, setValue}) => {
  const attachment = attachments[index]
  if (!attachment) return <></>

  return (
    <>
      {index !== 0 && <Spacer size={15} />}
      <div className={styles.attachmentField}>
        <FileField
          label={attachment.display_name}
          name={`attachments.${index}.file`}
          placeholder="ファイルを選択 または ドラッグ＆ドロップ"
          hint={attachment.note}
          hintWidth="250px"
        >
          {attachment?.file && (
            <FileField.FileContainer>
              <FileField.File
                file={attachment?.file}
                onRemove={() => setValue(`attachments.${index}.file`, undefined)}
              />
            </FileField.FileContainer>
          )}
        </FileField>
      </div>
    </>
  )
}

const targetEmployees = (selected: {[key: number]: Item}, selectedCount: number) => {
  const selectedNames = Object.entries(selected)
    .filter(([_, v]) => v)
    .map(([_, {name}]) => name)
  const lastIndex = selectedCount - 1
  return selectedCount > 1 ? `${selectedNames[lastIndex]}\n(他${lastIndex}名)` : selectedNames[lastIndex]
}

export const EgovPrepareModal: FC<EgovPrepareModalProps> = ({
  selected,
  procedureKind,
  setValidationErrors,
  refetch,
  isTaskRunning,
  isActedUser,
  officeIds,
}) => {
  const selectedCount = reduce((sum, value) => (value ? sum + 1 : sum), 0, selected)
  const downloadable = selectedCount > 0
  const procedureStatusIds = keys(pickBy(selected))
  const insuranceType = insuranceTypeByProcedureKind(procedureKind)
  const destinationType = insuranceType === 'social_insurance' ? 'pensionOffice' : 'employmentInsurance'
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const [fetchCsvEgovInformations, {loading, data}] = useLazyQuery<QueryResult, QueryVariables>(CSV_EGOV_INFORMATION, {
    fetchPolicy: 'network-only',
  })
  const recommendedAttachments = data?.client.csvEgovInformation.recommendedAttachments
  const [prepareCsvProcedure] = useMutation(PREPARE_CSV_PROCEDURE)
  const [asyncPrepareCsvProcedure] = useMutation(ASYNC_PREPARE_CSV_PROCEDURE)
  const defaultAttachments = [{document_name: 'その他', display_name: 'その他', file: undefined}]
  const disabledReason = isTaskRunning ? (isActedUser ? '申請準備中です' : '別の管理者が申請準備中です') : ''

  const methods = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      procedure_status_ids: [],
      procedure_kind: procedureKind,
      procedure_code: '',
      serial_number: '',
      submit_on: moment().format('YYYY/MM/DD'),
      attachments: defaultAttachments,
    },
  })

  const {
    handleSubmit: handleReactHookFormSubmit,
    register,
    setValue,
    trigger,
    watch,
    control,
    formState: {errors, isSubmitting, isValid},
  } = methods
  const {fields} = useFieldArray({
    control,
    name: 'attachments',
  })

  const handleClose = () => setIsOpen(false)
  const prepare = async (data: PrepareCsvProcedureSchema) => {
    try {
      const result = await prepareCsvProcedure({
        variables: {
          input: {
            procedureStatusIds: procedureStatusIds,
            officeIds: officeIds,
            procedureKind: data.procedure_kind,
            procedureCode: data.procedure_code,
            serialNumber: data.serial_number,
            submitOn: data.submit_on,
            attachments: data.attachments.filter((a) => !!a?.file),
          },
        },
      })

      const errors = result.data.prepareCsvProcedure.errors
      if (errors.length > 0) {
        setValidationErrors(errors)
      } else {
        setValidationErrors([])
        dispatch(notifySuccess('電子申請の申請準備が完了しました'))
      }
      setIsOpen(false)
    } catch (err) {
      dispatch(asyncError(err))
    }
  }

  const prepareRequest = async (data: PrepareCsvProcedureSchema) => {
    try {
      await asyncPrepareCsvProcedure({
        variables: {
          input: {
            procedureStatusIds: procedureStatusIds,
            officeIds: officeIds,
            procedureKind: data.procedure_kind,
            procedureCode: data.procedure_code,
            serialNumber: data.serial_number,
            submitOn: data.submit_on,
            attachments: data.attachments.filter((a) => !!a?.file),
          },
        },
      })
      refetch()
      setIsOpen(false)
      window.scrollTo(0, 0)
    } catch (err) {
      dispatch(asyncError(err))
    }
  }

  const handleSubmit = (data: PrepareCsvProcedureSchema) =>
    procedureStatusIds.length > MAX_SELECT_COUNT ? prepareRequest(data) : prepare(data)

  useEffect(() => {
    if (isOpen) {
      fetchCsvEgovInformations({
        variables: {procedureKind},
      })
    }

    setValue('submit_on', moment().format('YYYY/MM/DD'))
    if (data) {
      setValue('serial_number', data.client.egovSerialNumber[destinationType])
      trigger(['serial_number'])
    }
  }, [isOpen])

  useEffect(() => {
    if (recommendedAttachments) {
      const attachments = recommendedAttachments.map((recommended: RecommendedAttachment) => ({
        document_name: recommended.key,
        display_name: recommended.displayName,
        note: recommended.note,
        file: undefined,
      }))
      setValue('attachments', [...attachments, ...defaultAttachments])
    }
  }, [recommendedAttachments])

  useEffect(() => {
    if (data) {
      setValue('procedure_code', data.client.csvEgovInformation.procedureCode)
      setValue('serial_number', data.client.egovSerialNumber[destinationType])

      trigger(['procedure_code', 'serial_number'])
    }
  }, [data])

  const attachments = watch('attachments')

  return (
    <>
      <Button
        disabled={!downloadable || isTaskRunning}
        disabledReason={disabledReason}
        primary
        onClick={() => setIsOpen(true)}
      >
        申請準備する
      </Button>

      <Modal isOpen={isOpen} onClose={handleClose} className={styles.egovModal}>
        <FormProvider {...methods}>
          <form onSubmit={handleReactHookFormSubmit(handleSubmit)}>
            <Modal.Header onClose={handleClose}>新規申請内容</Modal.Header>
            <Modal.Body>
              {isOpen && loading ? (
                <Loading />
              ) : (
                <>
                  <ListGroup>
                    <ListGroup.Title>帳票一覧</ListGroup.Title>
                    <ListGroup.Item>{PROCEDURE_NAMES[procedureKind]}</ListGroup.Item>
                  </ListGroup>

                  <ListGroup>
                    <ListGroup.Title>対象従業員</ListGroup.Title>
                    <ListGroup.Item>{targetEmployees(selected, selectedCount)}</ListGroup.Item>
                  </ListGroup>

                  <Spacer size={20} />

                  <Label>
                    {insuranceType === 'social_insurance' ? '媒体通番' : 'FD通番'}
                    <Spacer direction="x" size={4} />
                    <Required />
                  </Label>
                  <Spacer size={8} />
                  <Input {...register('serial_number')} isError={!!errors.serial_number?.message} maxLength={3} />
                  {errors.serial_number?.message && <ErrorMessage>{errors.serial_number.message}</ErrorMessage>}

                  <Spacer size={20} />

                  <DateField label="提出年月日" required error={errors.submit_on?.message} name="submit_on" />

                  <Spacer size={20} />

                  <Section title="添付書類">
                    {fields.map((field, index) => (
                      <AttachmentFileField key={field.id} attachments={attachments} index={index} setValue={setValue} />
                    ))}
                  </Section>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <ButtonRow>
                <Button onClick={handleClose}>キャンセル</Button>
                <Button
                  primary
                  disabled={isSubmitting || !isValid || isTaskRunning}
                  onClick={handleReactHookFormSubmit(handleSubmit)}
                >
                  申請準備する
                </Button>
              </ButtonRow>
            </Modal.Footer>
          </form>
        </FormProvider>
      </Modal>
    </>
  )
}
